import { useMemo, useRef, useState } from 'react';
import ConnectArrowIcon from '../../assets/icons/ConnectArrowIcon';
import WarningIcon from '../../assets/icons/WarningIcon';
import { useAppearance } from '../../hooks/useAppearance';
import useLocales from '../../hooks/useLocales';
import { useRouter } from '../../hooks/useRouter';
import { useWalletConnector } from '../../hooks/useWalletConnectors';
import { RoutePath } from '../../pages';
import { ConnectorType, EmailRegExp } from '../../types';
import Button from '../button';
import CircleSpinner from '../circleSpinner';
import {
  EmailLoginContainer,
  StyleConnectArrowIcon,
  StyleEmailInput,
  StyleEmailInputWrapper,
  StyleInputError,
} from './styles';

const EmailLogin = () => {
  const { navigate } = useRouter();
  const inputRef = useRef<HTMLInputElement>(null);
  const locales = useLocales();
  const { appearance } = useAppearance();

  const [emailError, setEmailError] = useState<string>('');
  const [emailValue, setEmailValue] = useState<string>('');
  const [isFocus, setIsFocus] = useState<boolean>(false);

  const [loading, setLoading] = useState<boolean>(false);

  const walletConnector = useWalletConnector('particleAuth');

  const sendCode = async () => {
    try {
      const emailValue = inputRef.current?.value?.trim();
      if (emailValue) {
        // 1. 校验邮箱格式
        if (!emailValue) {
          throw new Error(locales.enterEmail);
        } else if (!EmailRegExp.test(emailValue)) {
          throw new Error(locales.emailFormatError);
        }
        setLoading(true);
        // 2. 发送验证码
        await walletConnector?.auth.getConnectCaptcha({ email: emailValue });
        // 3. 跳转到验证码输入页面
        navigate(RoutePath.Captcha, {
          value: emailValue,
          type: ConnectorType.Email,
        });
      }
    } catch (error: any) {
      console.log('error', error);
      let message = error?.extra?.[0] || error.message || (typeof error === 'string' ? error : locales.networkError);
      if (message.includes('Network Error')) {
        message = locales.networkError;
      }
      setEmailError(message);
    }
    setLoading(false);
  };

  const buttonDisabled = useMemo(() => {
    return !emailValue || !EmailRegExp.test(emailValue) || loading;
  }, [emailValue, loading]);

  /**
   * hide continue button
   */
  const hideContinueButton = useMemo(() => {
    return appearance?.hideContinueButton;
  }, [appearance?.hideContinueButton]);

  return (
    <EmailLoginContainer>
      <StyleEmailInputWrapper>
        <StyleEmailInput
          placeholder={locales.email}
          ref={inputRef}
          error={emailError}
          onChange={(e) => {
            setEmailError('');
            setEmailValue((e.target.value || '').trim());
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              if (!loading && EmailRegExp.test(emailValue)) {
                sendCode();
              }
            }
          }}
          onFocus={() => {
            setIsFocus(true);
          }}
          onBlur={() => {
            setIsFocus(false);
          }}
        />
        {hideContinueButton && (
          <StyleConnectArrowIcon
            onClick={() => {
              if (!loading && EmailRegExp.test(emailValue)) {
                sendCode();
              }
            }}
            disabled={buttonDisabled}
            $canClick={emailValue?.length >= 1}
            $isError={!!emailError}
          >
            <CircleSpinner
              logo={
                <div className='icon-0x7ff'>
                  <ConnectArrowIcon />
                </div>
              }
              connecting={loading}
              inset={0}
              width={30}
              height={30}
            />
          </StyleConnectArrowIcon>
        )}
      </StyleEmailInputWrapper>
      {emailError && (
        <StyleInputError>
          <div>
            <WarningIcon />
          </div>
          <div>{emailError}</div>
        </StyleInputError>
      )}

      {!hideContinueButton && (
        <Button style={{ marginTop: 16 }} onClick={sendCode} loading={loading} disabled={buttonDisabled}>
          {locales.continue}
        </Button>
      )}
    </EmailLoginContainer>
  );
};

export default EmailLogin;
